import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";
import { MaskedLines, Reveal, Chapter, Marquee, EASE } from "../components/motion";
import { ButtonLink } from "../components/CTA";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../components/ui/hover-card";
import { organizationLd, websiteLd, localBusinessLd } from "../lib/schema";

const HERO_IMG =
  "https://images.unsplash.com/photo-1716396635935-b7ba3d91a1cb?auto=format&fit=crop&w=1400&q=70";

const productCards = [
  {
    to: "/products/vyayanhub",
    name: "VyayanHub",
    tag: "Live",
    tagCls: "bg-emerald-100 text-emerald-700",
    desc: "Gym management for Indian fitness businesses — memberships, renewals, check-ins, payments and owner reports in one calm system.",
    longDesc: "The operating system for Indian gyms: front desk, trainers, payments and owner reporting in one place, with WhatsApp built in from day one.",
    points: ["Renewal reminders over WhatsApp, automatically", "UPI-friendly dues and payment tracking", "Multi-branch owner reports, always current"],
    external: "https://vyayamhub.com",
  },
  {
    to: "/products/society-management",
    name: "Society Management",
    tag: "Coming soon",
    tagCls: "bg-gray-100 text-gray-500",
    desc: "Billing, visitors, notices and complaints for housing societies and RWAs — without the WhatsApp chaos.",
    longDesc: "One system for the committee, one app for residents — built for Indian housing societies, not adapted from somewhere else.",
    points: ["Maintenance billing & collection tracking", "Visitor & delivery approvals at the gate", "Notices with read tracking, not group spam"],
  },
  {
    to: "/products/restaurant-management",
    name: "Restaurant Management",
    tag: "Coming soon",
    tagCls: "bg-gray-100 text-gray-500",
    desc: "Tables, orders, KOTs and inventory for independent restaurants that want one system, not five apps.",
    longDesc: "Front-of-house to kitchen in one calm system, designed to keep billing through a Friday-night internet dropout.",
    points: ["Tables, orders & KOT routing by station", "Inventory that deducts as dishes sell", "Owner reports ready at closing time"],
  },
];

const serviceCards = [
  {
    to: "/services/custom-software-development",
    num: "01",
    name: "Custom Software Development",
    desc: "Bespoke web apps, SaaS products, mobile apps and workflow automation shaped around how your business actually runs.",
    longDesc: "Software shaped around your operations — not the other way around. Weekly demos, fixed estimates, and support that stays after launch.",
    points: ["Bespoke web apps, SaaS & mobile", "Legacy modernisation without the big bang", "AMC support after launch"],
  },
  {
    to: "/services/cloud-solutions",
    num: "02",
    name: "Cloud Solutions",
    desc: "Architecture, migration, DevOps and cost optimisation on AWS, Azure and GCP — infrastructure that stays invisible.",
    longDesc: "Quiet, reliable cloud — planned in stages so the business never feels the move, and documented so no one is held hostage to a setup.",
    points: ["Staged migration to AWS/Azure/GCP", "CI/CD that makes releases routine", "Cost audits — most teams overpay 20–40%"],
  },
  {
    to: "/services/salesforce-development",
    num: "03",
    name: "Salesforce Development",
    desc: "Implementation, configuration and custom Apex development — independent, experience-led, no partner theatre.",
    longDesc: "Hands-on Salesforce delivery as an independent engineering partner — configuration first, custom code only where it earns its keep.",
    points: ["Sales Cloud & Service Cloud setup", "Apex & Lightning Web Components", "Clean data migration with sign-off reports"],
  },
  {
    to: "/services/data-and-ai",
    num: "04",
    name: "Data & AI",
    desc: "LLM apps, document intelligence and Indian-language AI — shipped into our own products first.",
    longDesc: "Practical AI applied to dues, documents, forecasts and front desks — piloted in our own products before it enters your proposal.",
    points: ["Chatbots & RAG over your documents", "Invoice, KYC & statement extraction", "Assess → Pilot → Scale engagement path"],
  },
];

const principles = [
  { num: "01", title: "Small, senior team", body: "The people you meet on the first call are the people who build. No hand-offs, no telephone game." },
  { num: "02", title: "We ship, then stay", body: "Launch is the midpoint, not the finish line. Products and client systems get the same long-term care." },
  { num: "03", title: "Built for Indian operations", body: "UPI, WhatsApp, GST, patchy connectivity — we design for the realities of running a business here." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <main data-testid="home-page">
      <SEO
        title="Adika Tech — Software Products & Engineering Services, Mumbai"
        description="Adika Tech builds software products like VyayanHub for gyms, and delivers custom software and cloud engineering for Indian businesses. Based in Mumbai."
        path="/"
        jsonLd={[organizationLd, websiteLd, localBusinessLd]}
      />

      <section ref={heroRef} className="relative overflow-hidden" data-testid="home-hero">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 pb-24 pt-12 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-28 lg:pt-16">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="text-xs font-semibold uppercase tracking-[0.22em] text-brand"
              data-testid="home-hero-overline"
            >
              Adika Tech · Software Studio · Mumbai
            </motion.p>
            <MaskedLines
              testId="home-hero-headline"
              lines={["Software that runs", "the businesses", "that run India."]}
              lineClassName="text-5xl sm:text-6xl lg:text-[76px] font-bold tracking-tighter leading-[1.04] text-gray-950"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
              className="mt-7 max-w-xl text-base leading-relaxed text-gray-600 lg:text-lg"
              data-testid="home-hero-subtext"
            >
              We build focused products — like VyayanHub for gyms — and craft custom software for
              teams that have outgrown spreadsheets, WhatsApp groups and manual workarounds.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <ButtonLink to="/products/vyayanhub" testId="home-hero-products-cta">Explore products</ButtonLink>
              <ButtonLink to="/services/custom-software-development" variant="outline" testId="home-hero-services-cta">
                Engineering services
              </ButtonLink>
            </motion.div>
          </div>
          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
              className="relative"
            >
              <div className="clip-frame relative aspect-[4/5] overflow-hidden rounded-sm" data-testid="home-hero-image-frame">
                <motion.img
                  src={HERO_IMG}
                  alt="Mumbai skyline at dusk — where Adika Tech is built"
                  style={{ y: imgY }}
                  className="h-[115%] w-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950/25 to-transparent" />
              </div>
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -left-6 hidden rounded-sm border border-gray-200 bg-white px-5 py-4 shadow-lg lg:block"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Based in</p>
                <p className="mt-1 font-heading text-lg font-bold text-gray-950">Mumbai, India</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 lg:block"
        >
          <ArrowDown className="h-5 w-5 animate-bounce text-gray-300" aria-hidden="true" />
        </motion.div>
      </section>

      <Marquee items={["VyayanHub", "Gym Management", "Custom Software", "Cloud Solutions", "Made in Mumbai", "For Indian Business"]} />

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32" data-testid="home-paths-section">
        <Chapter number="01" overline="Two ways to work with us" title="Use what we've built. Or have us build for you." testId="home-chapter-01" />
        <div className="mt-16 grid grid-equal gap-8 lg:grid-cols-2">
          <Reveal>
            <Link
              to="/products/vyayanhub"
              data-testid="home-path-products"
              className="group flex h-full flex-col justify-between rounded-sm border border-gray-200 bg-white p-8 transition-[box-shadow,border-color] duration-300 hover:border-brand hover:shadow-xl lg:p-12"
            >
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">Products</p>
                <h3 className="mt-4 font-heading text-3xl font-bold tracking-tight text-gray-950 lg:text-4xl">
                  VyayanHub, live today. Two more on the way.
                </h3>
                <p className="mt-4 max-w-md text-base leading-relaxed text-gray-600">
                  Purpose-built software for gyms, housing societies and restaurants — priced for
                  Indian businesses, supported by the people who wrote it.
                </p>
              </div>
              <span className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-gray-950">
                See the products
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </Link>
          </Reveal>
          <Reveal delay={0.12}>
            <Link
              to="/services/custom-software-development"
              data-testid="home-path-services"
              className="group flex h-full flex-col justify-between rounded-sm bg-gray-950 p-8 text-white transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-2xl lg:p-12"
            >
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Services</p>
                <h3 className="mt-4 font-heading text-3xl font-bold tracking-tight lg:text-4xl">
                  Custom software & cloud engineering.
                </h3>
                <p className="mt-4 max-w-md text-base leading-relaxed text-gray-300">
                  For operations heads and founders who need a system that fits — not another tool
                  to work around.
                </p>
              </div>
              <span className="mt-10 inline-flex items-center gap-2 text-sm font-semibold">
                Start a conversation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-24 lg:py-32" data-testid="home-products-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Chapter number="02" overline="Products" title="One problem each. Solved properly." testId="home-chapter-02" />
          <div className="mt-16 grid grid-equal gap-8 lg:grid-cols-3">
            {productCards.map((p, i) => (
              <Reveal key={p.to} delay={i * 0.06}>
                <HoverCard openDelay={150} closeDelay={120}>
                  <HoverCardTrigger asChild>
                    <div
                      data-testid={`home-product-card-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="group flex h-full flex-col rounded-sm border border-gray-200 bg-white p-8 transition-[box-shadow,border-color,transform] duration-200 ease-out hover:-translate-y-1 hover:border-brand hover:shadow-xl"
                    >
                      <span className={`w-fit rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${p.tagCls}`}>{p.tag}</span>
                      <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight text-gray-950">{p.name}</h3>
                      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-gray-600">{p.desc}</p>
                      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 pt-8">
                        <Link to={p.to} className="inline-flex items-center gap-2 text-sm font-semibold text-brand">
                          Learn more
                          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                        </Link>
                        {p.external && (
                          <a
                            href={p.external}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="home-product-vyayamhub-link"
                            className="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 transition-colors duration-200 hover:text-brand"
                          >
                            vyayamhub.com
                            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                          </a>
                        )}
                      </div>
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent side="top" className="w-80 rounded-sm border-gray-200 bg-white p-5 shadow-xl">
                    <p className="font-heading text-base font-bold text-gray-950">{p.name}</p>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600">{p.longDesc}</p>
                    <ul className="mt-3 space-y-1.5">
                      {p.points.map((pt) => (
                        <li key={pt} className="flex gap-2 text-xs leading-relaxed text-gray-600">
                          <span aria-hidden="true" className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                    <Link to={p.to} data-testid={`home-product-popup-cta-${i}`} className="mt-4 inline-flex items-center gap-1.5 rounded-sm bg-brand px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-brand-dark">
                      View product <ArrowRight className="h-3 w-3" aria-hidden="true" />
                    </Link>
                  </HoverCardContent>
                </HoverCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32" data-testid="home-services-section">
        <Chapter number="03" overline="Services" title="Engineering capacity, without the agency theatre." testId="home-chapter-03" />
        <div className="mt-16 grid grid-equal gap-8 lg:grid-cols-2">
          {serviceCards.map((s, i) => (
            <Reveal key={s.to} delay={i * 0.06}>
              <HoverCard openDelay={150} closeDelay={120}>
                <HoverCardTrigger asChild>
                  <div
                    data-testid={`home-service-card-${s.num}`}
                    className="group relative flex h-full flex-col overflow-hidden rounded-sm border border-gray-200 p-8 transition-[box-shadow,border-color,transform] duration-200 ease-out hover:-translate-y-1 hover:border-brand hover:shadow-xl lg:p-10"
                  >
                    <span aria-hidden="true" className="absolute -right-4 -top-8 font-heading text-[7rem] font-extrabold leading-none text-gray-100">
                      {s.num}
                    </span>
                    <h3 className="relative font-heading text-2xl font-bold tracking-tight text-gray-950 lg:text-3xl">{s.name}</h3>
                    <p className="relative mt-4 line-clamp-3 text-base leading-relaxed text-gray-600">{s.desc}</p>
                    <span className="relative mt-auto pt-8">
                      <Link to={s.to} className="inline-flex items-center gap-2 text-sm font-semibold text-brand">
                        Explore the service
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                      </Link>
                    </span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="top" className="w-80 rounded-sm border-gray-200 bg-white p-5 shadow-xl">
                  <p className="font-heading text-base font-bold text-gray-950">{s.name}</p>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{s.longDesc}</p>
                  <ul className="mt-3 space-y-1.5">
                    {s.points.map((pt) => (
                      <li key={pt} className="flex gap-2 text-xs leading-relaxed text-gray-600">
                        <span aria-hidden="true" className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                  <Link to={s.to} data-testid={`home-service-popup-cta-${s.num}`} className="mt-4 inline-flex items-center gap-1.5 rounded-sm bg-brand px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-brand-dark">
                    Explore the service <ArrowRight className="h-3 w-3" aria-hidden="true" />
                  </Link>
                </HoverCardContent>
              </HoverCard>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-gray-200 bg-white py-24 lg:py-32" data-testid="home-ai-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Chapter number="04" overline="Data & AI" title="AI we ship into our own products first." testId="home-chapter-04" />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-gray-600" data-testid="home-ai-intro">
              We don't sell AI we haven't run ourselves. These capabilities are being built into
              VyayanHub and our upcoming products — proven in production there before they ever
              appear in a client proposal.
            </p>
          </Reveal>
          <div className="mt-16 grid grid-equal gap-8 lg:grid-cols-3">
            {[
              {
                product: "In VyayanHub",
                items: [
                  "Member churn prediction from attendance and payment patterns",
                  "WhatsApp assistant answering member queries on dues, timings and plans",
                  "Face-recognition attendance, building on our biometric integrations",
                ],
              },
              {
                product: "In Society Management",
                items: [
                  "Complaint intake and routing in Hindi or Marathi, over WhatsApp",
                  "Gate CCTV analytics — unrecognised vehicle and visitor alerts",
                  "Natural-language queries over society accounts for committees",
                ],
              },
              {
                product: "In Restaurant Management",
                items: [
                  "Demand forecasting to cut food waste and stockouts",
                  "WhatsApp AI order-taking for regulars",
                  "GST invoice and supplier-bill extraction",
                ],
              },
            ].map((g, i) => (
              <Reveal key={g.product} delay={i * 0.1}>
                <div className="h-full rounded-sm border border-gray-200 p-8 transition-[box-shadow,border-color] duration-300 hover:border-brand hover:shadow-lg" data-testid={`home-ai-group-${i}`}>
                  <h3 className="font-heading text-xl font-bold tracking-tight text-gray-950">{g.product}</h3>
                  <ul className="mt-5 space-y-3">
                    {g.items.map((item) => (
                      <li key={item} className="flex gap-3 text-sm leading-relaxed text-gray-600">
                        <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-col items-start justify-between gap-6 rounded-sm bg-brand-tint p-8 sm:flex-row sm:items-center lg:p-10">
              <p className="max-w-xl text-sm leading-relaxed text-gray-700">
                Want this in your product? We take on a small number of AI build partnerships each
                year — start with a discovery engagement.
              </p>
              <ButtonLink to="/contact" testId="home-ai-cta">Talk to us about AI</ButtonLink>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-gray-950 py-24 text-white lg:py-32" data-testid="home-manifesto-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">How we work</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 max-w-3xl font-heading text-4xl tracking-tight lg:text-5xl">
              A studio, not a factory.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-12 lg:grid-cols-3">
            {principles.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.12}>
                <div className="border-t border-gray-800 pt-8" data-testid={`home-principle-${p.num}`}>
                  <span className="font-heading text-sm font-bold text-brand">{p.num}</span>
                  <h3 className="mt-3 font-heading text-xl font-bold">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-400">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32" data-testid="home-cta-section">
        <Reveal>
          <div className="rounded-sm border border-gray-200 bg-brand-tint p-10 lg:p-16">
            <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand">Next step</p>
                <h2 className="mt-4 max-w-2xl font-heading text-4xl tracking-tight text-gray-950 lg:text-5xl">
                  Tell us what's slowing your business down.
                </h2>
              </div>
              <div className="flex flex-wrap gap-4">
                <ButtonLink to="/products/vyayanhub#demo" testId="home-cta-demo">Book a demo</ButtonLink>
                <ButtonLink to="/contact" variant="outline" testId="home-cta-enquiry">Send an enquiry</ButtonLink>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
